<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Horarios de Pico',
  },
});

const peakHours = computed(() => props.data?.peak_hours || []);
const quietHours = computed(() => props.data?.quiet_hours || []);
const busiestDay = computed(() => props.data?.busiest_day || '-');
const busiestDayCount = computed(() => props.data?.busiest_day_count || 0);
const avgPerHour = computed(() => props.data?.average_per_hour || 0);
const avgPerDay = computed(() => props.data?.average_per_day || 0);

function formatHour(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-activity text-lg text-n-amber-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Peak Hours -->
      <div>
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-trending-up text-sm text-n-ruby-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Horarios mais movimentados</span>
        </div>
        <div class="flex gap-2">
          <div
            v-for="(hour, index) in peakHours"
            :key="`peak-${index}`"
            class="flex-1 text-center p-2 rounded-lg"
            :class="index === 0 ? 'bg-n-ruby-3' : 'bg-n-alpha-1'"
          >
            <div
              class="text-lg font-bold"
              :class="index === 0 ? 'text-n-ruby-11' : 'text-n-slate-12'"
            >
              {{ formatHour(hour.hour) }}
            </div>
            <div
              class="text-xs"
              :class="index === 0 ? 'text-n-ruby-10' : 'text-n-slate-11'"
            >
              {{ hour.count }} conv.
            </div>
          </div>
        </div>
      </div>

      <!-- Quiet Hours -->
      <div>
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-trending-down text-sm text-n-teal-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Horarios mais calmos</span>
        </div>
        <div class="flex gap-2">
          <div
            v-for="(hour, index) in quietHours"
            :key="`quiet-${index}`"
            class="flex-1 text-center p-2 rounded-lg bg-n-alpha-1"
          >
            <div class="text-lg font-bold text-n-teal-11">
              {{ formatHour(hour.hour) }}
            </div>
            <div class="text-xs text-n-slate-11">{{ hour.count }} conv.</div>
          </div>
        </div>
      </div>

      <!-- Summary stats -->
      <div class="grid grid-cols-2 gap-3 pt-3 border-t border-n-weak">
        <div class="p-3 rounded-lg bg-n-amber-3">
          <div class="text-xs text-n-amber-11 mb-1">Dia mais movimentado</div>
          <div class="text-lg font-bold text-n-amber-11">{{ busiestDay }}</div>
          <div class="text-xs text-n-amber-10">
            {{ busiestDayCount }} conversas
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="grid grid-cols-2 gap-2">
            <div class="text-center">
              <div class="text-lg font-bold text-n-slate-12">
                {{ avgPerHour }}
              </div>
              <div class="text-xs text-n-slate-11">por hora</div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-n-slate-12">
                {{ avgPerDay }}
              </div>
              <div class="text-xs text-n-slate-11">por dia</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
